import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Briefcase, MapPin, Clock, Users, CheckCircle, Star, ArrowRight, 
  Send, Download, Award, Lightbulb, Target, MessageSquare, Menu, X,
  ChevronDown, Code, Palette, TrendingUp, Building2, Heart, Zap, Mail,
  Phone, Globe, Github, Linkedin, ExternalLink, Calendar, BookOpen,
  BarChart3, User, FileText
} from 'lucide-react';

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import api from '../lib/api';
import { useToast } from '../hooks/use-toast';
import '../styles/Career.css';

gsap.registerPlugin(ScrollTrigger);

const Career = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [activeTab, setActiveTab] = useState('benefits');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const heroRef = useRef(null);
  const opportunitiesRef = useRef(null);
  const googleFormUrl = 'https://forms.gle/C46L1DvgsCwALXjVA';

  const opportunities = [
    {
      id: 1,
      field: 'Frontend Development',
      icon: Code,
      color: 'from-blue-500 to-cyan-500',
      positions: [
        { title: 'React Developer Intern', level: 'Fresher', duration: '3-6 months' },
        { title: 'UI/UX Developer Intern', level: 'Fresher', duration: '3-6 months' },
        { title: 'Web Developer Intern', level: 'Fresher', duration: '3-6 months' }
      ],
      skills: ['React', 'Tailwind CSS', 'JavaScript', 'Git']
    },
    {
      id: 2,
      field: 'UI/UX Design',
      icon: Palette,
      color: 'from-purple-500 to-pink-500',
      positions: [
        { title: 'UI Designer Intern', level: 'Fresher', duration: '3-6 months' },
        { title: 'UX Designer Intern', level: 'Fresher', duration: '3-6 months' },
        { title: 'Product Designer Intern', level: 'Fresher', duration: '3-6 months' }
      ],
      skills: ['Figma', 'Adobe XD', 'Wireframing', 'Design Thinking']
    },
    {
      id: 3,
      field: 'Backend Development',
      icon: Building2,
      color: 'from-green-500 to-emerald-500',
      positions: [
        { title: 'Node.js Developer Intern', level: 'Fresher', duration: '3-6 months' },
        { title: 'Database Developer Intern', level: 'Fresher', duration: '3-6 months' },
        { title: 'API Developer Intern', level: 'Fresher', duration: '3-6 months' }
      ],
      skills: ['Node.js', 'MongoDB', 'Express', 'REST APIs']
    },
    {
      id: 4,
      field: 'Data Analytics',
      icon: BarChart3,
      color: 'from-orange-500 to-red-500',
      positions: [
        { title: 'Data Analyst Intern', level: 'Fresher', duration: '3-6 months' },
        { title: 'BI Developer Intern', level: 'Fresher', duration: '3-6 months' },
        { title: 'Analytics Engineer Intern', level: 'Fresher', duration: '3-6 months' }
      ],
      skills: ['Python', 'SQL', 'Tableau', 'Excel']
    },
    {
      id: 5,
      field: 'Marketing & Growth',
      icon: TrendingUp,
      color: 'from-yellow-500 to-orange-500',
      positions: [
        { title: 'Marketing Intern', level: 'Fresher', duration: '3-6 months' },
        { title: 'Growth Hacker Intern', level: 'Fresher', duration: '3-6 months' },
        { title: 'Content Marketing Intern', level: 'Fresher', duration: '3-6 months' }
      ],
      skills: ['SEO', 'Social Media', 'Analytics', 'Content Writing']
    },
    {
      id: 6,
      field: 'Business & Operations',
      icon: Briefcase,
      color: 'from-indigo-500 to-purple-500',
      positions: [
        { title: 'Business Analyst Intern', level: 'Fresher', duration: '3-6 months' },
        { title: 'Operations Intern', level: 'Fresher', duration: '3-6 months' },
        { title: 'Customer Success Intern', level: 'Fresher', duration: '3-6 months' }
      ],
      skills: ['Data Analysis', 'Communication', 'Project Management', 'Excel']
    }
  ];

  const benefits = [
    { icon: Award, title: 'Hands-on Experience', description: 'Work on real-world projects with mentorship from industry experts' },
    { icon: Lightbulb, title: 'Learning & Development', description: 'Access to courses, workshops, and learning resources' },
    { icon: Users, title: 'Networking Opportunities', description: 'Connect with talented professionals and grow your network' },
    { icon: Heart, title: 'Flexible Duration', description: 'Choose between 3-6 months based on your availability' },
    { icon: Star, title: 'Certificate & Recommendation', description: 'Get official certificate and LinkedIn recommendation letter' },
    { icon: Zap, title: 'Performance Bonus', description: 'Potential for performance-based incentives and job offers' }
  ];

  const timeline = [
    { step: 1, title: 'Application Submission', description: 'Submit your application through our online form with your details and documents' },
    { step: 2, title: 'Initial Screening', description: 'Our team reviews your profile and qualifications' },
    { step: 3, title: 'Technical Assessment', description: 'Take a skill-based test relevant to your chosen field' },
    { step: 4, title: 'Interview Round', description: 'Face-to-face interview with the team leads' },
    { step: 5, title: 'Final Selection', description: 'Get selected and receive your offer letter' },
    { step: 6, title: 'Onboarding', description: 'Join our team and start your internship journey' }
  ];

  // Handle Apply Now with authentication check
  const handleApplyNow = () => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (!token) {
      // User not authenticated - redirect to login
      toast({
        title: 'Login Required',
        description: 'Please create an account or login to apply for internships.',
        variant: 'default'
      });
      navigate('/login', { state: { from: '/career' } });
    } else {
      // User authenticated - open Google Form
      window.open(googleFormUrl, '_blank');
    }
  };

  const faqs = [
    {
      question: 'Who can apply for internships at Sync & Explore?',
      answer: 'We welcome applications from students and freshers (0-1 year experience) from all backgrounds and educational institutions. No specific GPA requirement, but we look for passion and willingness to learn.'
    },
    {
      question: 'What is the duration of the internship?',
      answer: 'Internships are flexible and can range from 3-6 months depending on your availability and project requirements. We can also discuss full-time opportunities for exceptional candidates.'
    },
    {
      question: 'Do you provide stipend/compensation?',
      answer: 'Yes, we offer competitive stipends based on your role and performance. Plus, there\'s a performance bonus opportunity at the end of your internship.'
    },
    {
      question: 'Can I work remotely?',
      answer: 'We offer both remote and hybrid opportunities depending on the role. Most internships can be done from home, making it perfect for students managing their studies.'
    },
    {
      question: 'What skills do I need to have?',
      answer: 'Basic knowledge in your chosen field is helpful, but we\'re more interested in your enthusiasm and ability to learn quickly. We provide training and mentorship throughout.'
    },
    {
      question: 'What happens after the internship?',
      answer: 'Outstanding performers may receive full-time job offers. Even if not, you\'ll get a certificate, LinkedIn recommendation, and be part of our alumni network.'
    }
  ];



  useEffect(() => {
    // Ensure hero text is always visible immediately
    if (heroRef.current) {
      heroRef.current.style.opacity = '1';
      heroRef.current.style.visibility = 'visible';
    }

    // Hero animation - now with clearProps to ensure final state is visible
    if (heroRef.current) {
      gsap.from(heroRef.current.children, {
        duration: 0.8,
        y: 30,
        opacity: 0,
        stagger: 0.2,
        ease: 'power3.out',
        onComplete: () => {
          // Ensure elements stay visible after animation
          if (heroRef.current) {
            gsap.set(heroRef.current.children, { 
              opacity: 1, 
              visibility: 'visible',
              clearProps: 'none'
            });
          }
        }
      });
    }

    // ScrollTrigger animations
    gsap.to('.opportunity-card', {
      scrollTrigger: {
        trigger: opportunitiesRef.current,
        start: 'top center'
      },
      duration: 0.6,
      y: 0,
      opacity: 1,
      stagger: 0.1,
      ease: 'power3.out'
    });
  }, []);

  // Handle scroll effect for navbar blur
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="w-full min-h-screen bg-white">
      {/* Navbar */}
      <nav className={`sticky top-0 z-50 border-none transition-all duration-300 ${
        isScrolled 
          ? 'backdrop-blur-xl bg-white/80 shadow-lg' 
          : 'backdrop-blur-none bg-transparent shadow-none'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img src="/images/sync_logo.png" alt="Sync & Explore" className="h-12 w-13" />
              <span className={`hidden sm:inline font-roboto text-2xl font-bold transition-colors duration-300 ${
                isScrolled ? 'text-[#1A8F98]' : 'text-[#1A8F98]'
              }`}>Sync & Explore</span>
            </Link>

            {/* Desktop Menu */}
            <ul className="hidden lg:flex items-center gap-8 font-roboto tracking-wide bg-gradient-to-r from-[#1A8F98] to-[#0891b2] rounded-full px-8 py-3 shadow-md">
              <li><button onClick={() => document.getElementById('opportunities').scrollIntoView({ behavior: 'smooth' })} className="text-white hover:text-cyan-100 transition-colors font-semibold">Opportunities</button></li>
              <li><button onClick={() => document.getElementById('benefits').scrollIntoView({ behavior: 'smooth' })} className="text-white hover:text-cyan-100 transition-colors font-semibold">Benefits</button></li>
              <li><button onClick={handleApplyNow} className="text-white hover:text-cyan-100 transition-colors font-semibold">Apply Now</button></li>
              <li><Link to="/contact" className="text-white hover:text-cyan-100 transition-colors font-semibold">Contact</Link></li>
              <li>
                <Link to="/" className="px-5 py-2 bg-white text-[#1A8F98] rounded-full hover:shadow-lg transition-all font-semibold">
                  Home
                </Link>
              </li>
            </ul>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden focus:outline-none"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden pb-4">
              <button onClick={() => { document.getElementById('opportunities').scrollIntoView({ behavior: 'smooth' }); setIsMobileMenuOpen(false); }} className="block w-full text-left py-2 text-gray-700 hover:text-[#1A8F98]">Opportunities</button>
              <button onClick={() => { document.getElementById('benefits').scrollIntoView({ behavior: 'smooth' }); setIsMobileMenuOpen(false); }} className="block w-full text-left py-2 text-gray-700 hover:text-[#1A8F98]">Benefits</button>
              <button onClick={() => { handleApplyNow(); setIsMobileMenuOpen(false); }} className="block w-full text-left py-2 text-gray-700 hover:text-[#1A8F98]">Apply Now</button>
              <Link to="/contact" className="block py-2 text-gray-700 hover:text-[#1A8F98]">Contact</Link>
              <Link to="/" className="block mt-2 px-4 py-2 bg-gradient-to-r from-[#1A8F98] to-[#0891b2] text-white rounded-lg text-center">Back Home</Link>
            </div>
          )}
        </div>
      </nav>

      {/* HERO SECTION */}
      <section className="py-28 px-4 sm:px-6 lg:px-8 relative overflow-hidden min-h-[600px] flex items-center justify-center" style={{
        backgroundImage: 'url(/images/mountains1.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        {/* Gradient Overlay for text visibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#6DDADF]/60 via-[#88E1E5]/30 to-[#97d7da]/60" style={{ zIndex: 1 }}></div>
        
        {/* Animated blobs */}
        <div className="absolute inset-0 overflow-hidden" style={{ zIndex: 2 }}>
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        </div>
        
        <div ref={heroRef} className="relative max-w-6xl mx-auto text-center" style={{ zIndex: 10 }}>
          <div className="mb-8 inline-block">
            <img src="/images/sync_logo.png" alt="Sync & Explore" className="h-28 w-30 transform hover:scale-110 transition-transform" style={{ 
              filter: 'drop-shadow(0 20px 25px rgba(0, 0, 0, 0.3))',
              textShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }} />
          </div>
          <h1 className="font-playfair text-6xl md:text-7xl font-bold mb-4 leading-tight text-white" style={{
            textShadow: '0 10px 30px rgba(0, 0, 0, 0.4), 0 2px 10px rgba(0, 0, 0, 0.3)'
          }}>
            Launch Your Career
          </h1>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white font-roboto tracking-wide" style={{
            textShadow: '0 8px 25px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.3)'
          }}>
            with Sync & Explore
          </h2>
          <p className="text-lg md:text-xl text-white mb-8 max-w-3xl mx-auto font-roboto" style={{
            textShadow: '0 8px 20px rgba(0, 0, 0, 0.35), 0 2px 8px rgba(0, 0, 0, 0.25)',
            lineHeight: '1.6'
          }}>
            Join our talented team as an intern and turn your passion into expertise. We're building the future of travel technology with passionate, talented individuals like you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button 
              onClick={handleApplyNow}
              className="px-8 py-4 bg-white text-[#1A8F98] font-bold font-roboto rounded-full hover:shadow-2xl hover:bg-opacity-95 transform hover:scale-105 transition-all flex items-center gap-2"
              style={{ zIndex: 20 }}
            >
              Apply Now <ArrowRight className="w-5 h-5" />
            </button>
            <button 
              onClick={() => document.getElementById('opportunities').scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 bg-white/25 text-white font-bold font-roboto rounded-full hover:bg-white/35 transition-all border-2 border-white backdrop-blur-sm"
              style={{ zIndex: 20, textShadow: '0 4px 8px rgba(0, 0, 0, 0.3)' }}
            >
              Explore Roles
            </button>
          </div>

          {/* Stats */}
          {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-12 border-t-2 border-white/40">
            <div>
              <div className="text-4xl font-bold text-white" style={{ textShadow: '0 4px 12px rgba(0, 0, 0, 0.4)' }}>50+</div>
              <p className="text-white/90 font-semibold" style={{ textShadow: '0 2px 6px rgba(0, 0, 0, 0.3)' }}>Active Interns</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-white" style={{ textShadow: '0 4px 12px rgba(0, 0, 0, 0.4)' }}>6</div>
              <p className="text-white/90 font-semibold" style={{ textShadow: '0 2px 6px rgba(0, 0, 0, 0.3)' }}>Career Paths</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-white" style={{ textShadow: '0 4px 12px rgba(0, 0, 0, 0.4)' }}>100%</div>
              <p className="text-white/90 font-semibold" style={{ textShadow: '0 2px 6px rgba(0, 0, 0, 0.3)' }}>Growth Rate</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-white" style={{ textShadow: '0 4px 12px rgba(0, 0, 0, 0.4)' }}>25+</div>
              <p className="text-white/90 font-semibold" style={{ textShadow: '0 2px 6px rgba(0, 0, 0, 0.3)' }}>Mentors</p>
            </div>
          </div> */}
        </div>
      </section>

      {/* OPPORTUNITIES SECTION */}
      <section id="opportunities" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-5xl font-bold text-[#1A8F98] mb-4">Internship Opportunities</h2>
            <p className="text-xl text-gray-600">Choose from diverse roles across multiple departments</p>
          </div>

          <div ref={opportunitiesRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {opportunities.map((opp) => {
              const IconComponent = opp.icon;
              return (
                <div 
                  key={opp.id}
                  className="opportunity-card group bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-teal-500 hover:shadow-2xl transition-all duration-300 opacity-0 cursor-pointer transform hover:-translate-y-2"
                  onClick={() => setSelectedRole(opp)}
                >
                  <div className={`w-14 h-14 rounded-lg bg-gradient-to-br ${opp.color} p-3 mb-4 text-white`}>
                    <IconComponent className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{opp.field}</h3>
                  
                  <div className="space-y-2 mb-6">
                    {opp.positions.map((pos, idx) => (
                      <div key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                        <span>{pos.title}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {opp.skills.slice(0, 2).map((skill, idx) => (
                      <span key={idx} className="px-3 py-1 text-xs text-white rounded-full" style={{
                        background: `linear-gradient(135deg, ${opp.color})`
                      }}>
                        {skill}
                      </span>
                    ))}
                    {opp.skills.length > 2 && (
                      <span className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">+{opp.skills.length - 2}</span>
                    )}
                  </div>

                  <button className="w-full py-2 text-[#1A8F98] font-semibold border-2 border-[#1A8F98] rounded-lg hover:bg-[#1A8F98] hover:bg-opacity-10 transition-all group-hover:bg-[#1A8F98] group-hover:text-white">
                    View Details <ArrowRight className="inline w-4 h-4 ml-2" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BENEFITS SECTION */}
      <section id="benefits" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#E8F5F7] to-[#F0FAFB]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-5xl font-bold text-[#1A8F98] mb-4">Why Join Us?</h2>
            <p className="text-xl text-gray-600">Comprehensive perks & development opportunities</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, idx) => {
              const IconComponent = benefit.icon;
              return (
                <div 
                  key={idx}
                  className="bg-white rounded-2xl p-8 border-l-4 border-[#1A8F98] hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#1A8F98] to-[#0891b2] flex items-center justify-center text-white mb-4">
                    <IconComponent className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SELECTION PROCESS TIMELINE */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-5xl font-bold text-[#1A8F98] mb-4">Selection Process</h2>
            <p className="text-xl text-gray-600">Simple, transparent, and fair evaluation</p>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-[#1A8F98] to-[#0891b2]"></div>
            
            <div className="space-y-8">
              {timeline.map((item, idx) => (
                <div key={idx} className={`flex items-center ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                  <div className={`w-full lg:w-1/2 ${idx % 2 === 0 ? 'lg:pr-12 lg:text-right' : 'lg:pl-12'}`}>
                    <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-[#1A8F98] hover:shadow-lg transition-all">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">Step {item.step}: {item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                  
                  <div className="hidden lg:flex w-full lg:w-auto justify-center mb-4 lg:mb-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#1A8F98] to-[#0891b2] text-white flex items-center justify-center font-bold text-lg relative z-10 border-4 border-white shadow-lg">
                      {item.step}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>



      {/* FAQ SECTION */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#E8F5F7] to-[#F0FAFB]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-5xl font-bold text-[#1A8F98] mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Got questions? We've got answers!</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden hover:border-[#1A8F98] transition-all"
              >
                <button 
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-gray-900 text-left">{faq.question}</h3>
                  <ChevronDown className={`w-6 h-6 text-[#1A8F98] transition-transform ${expandedFaq === idx ? 'transform rotate-180' : ''}`} />
                </button>
                
                {expandedFaq === idx && (
                  <div className="px-6 py-4 border-t-2 border-gray-100 bg-gray-50">
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h2 className="font-playfair text-2xl font-bold mb-4">Sync & Explore</h2>
              <p className="text-gray-400">Building the future of travel technology with passionate minds.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
                <li><button onClick={() => document.getElementById('opportunities').scrollIntoView({ behavior: 'smooth' })} className="hover:text-white transition-colors">Opportunities</button></li>
                <li><button onClick={handleApplyNow} className="hover:text-white transition-colors">Apply</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center gap-2"><Mail className="w-4 h-4" /> careers@syncexplore.com</li>
                <li className="flex items-center gap-2"><Phone className="w-4 h-4" /> +91 XXXX XXXXX</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Follow Us</h3>
              <div className="flex gap-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors"><Linkedin className="w-6 h-6" /></a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors"><Github className="w-6 h-6" /></a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Sync & Explore. All rights reserved. | <Link to="/terms-and-services" className="hover:text-white">Terms</Link> | <Link to="/privacy-policy" className="hover:text-white">Privacy</Link></p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Career;

